import React, { FunctionComponent } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

const { DateTime } = require("luxon");

import { NutriMail } from '../../../../models/NutriMail';
import { EhealthMail } from '../../../../models/EhealthMail';
import { ZorgMail } from '../../../../models/ZorgMail';
import { User } from '../../../../models/User';

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersEhealth from '../../../../actions/helpers/ehealth';
import HelpersZorgmail from '../../../../actions/helpers/zorgmail';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';
import { CallMade, CallReceived } from '@mui/icons-material';

// Any mail row, normalised across the three sources. `incoming` picks the
// arrow direction + which name half to show (sender when received, recipient
// when sent — the practice's point of view).
type MailRow = {
  id: number;
  incoming: boolean;
  unread: boolean;
  name: string;
  subject: string;
  created_at: string;
};

// One component, three sources. All differences between NutriMail, eHealth
// mail and ZorgMail live in this config: the query (object + fields + the
// received/sent advanced filters, lifted verbatim from each inbox page's
// client-scoped queries), the row normaliser, the module/init gate and the
// CTA target. Everything else (settings, counts line, last-5 list, layout)
// is shared.
type SourceKey = 'nutri' | 'ehealth' | 'zorg';

type SourceConfig = {
  valueKey: string;
  titleKey: string;
  ctaLabelKey: string;
  ctaId: string;
  // Module check for the premium lock (the sidebar's gate). NutriMail checks
  // the practice module; ehealth/zorg use their can_check_* helpers
  // (login type + practice module in one).
  moduleEnabled: (login: any, currentPractice: any) => boolean;
  // Credentials gate for ehealth/zorg (always true for nutri). Queries run
  // only when both this and moduleEnabled pass; the CTA opens the init
  // dialog when only this is missing.
  initialised: (login: any) => boolean;
  navigateState: (object: User) => any;
  // The received + sent queries are the inbox page's client-scoped filters:
  // mails from/to the viewed client only (nutri pairs the from/to ids,
  // ehealth/zorg match the client via client_id, resolved server-side on
  // the client's id or national number).
  fetch: (i18n: any, login: any, object: User, box: 'received' | 'sent') => Promise<{ result: any[]; count: number }>;
  normalise: (mail: any, incoming: boolean) => MailRow;
};

const CONFIG: Record<SourceKey, SourceConfig> = {
  nutri: {
    valueKey: HelpersClientWidgets.communicationNutriValueKey,
    titleKey: 'coach.submenu.detail.communication_nutri',
    ctaLabelKey: 'coach.general.actions.to_communication_nutri',
    ctaId: 'goto_communication_nutri',
    moduleEnabled: (login, currentPractice) =>
      !!HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'nutri_mail'}),
    initialised: () => true,
    navigateState: (object) => ({
      user_id: {id: object.id, name: `${object.first_name} ${object.last_name}`}
    }),
    fetch: (i18n, login, object, box) =>
      fetch_all_with_count<NutriMail>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'nutri_mail',
          fields: ['id', 'read', 'created_at', 'subject', 'from', 'to'],
          sub_fields: {
            from: ['id', 'full_name'],
            to: ['id', 'full_name']
          },
          per_page: 5,
          page: 0,
          order: 'created_at DESC',
          filter: {
            search: '',
            // The inbox page's exact advanced filters, paired on the viewed
            // client: received = addressed to me, from the client; sent =
            // from me, addressed to the client.
            advanced: box === 'received' ? {
              to_id: login?.id,
              from_id: object?.id,
              sent: 1,
              deleted: '0',
              pov: 1
            } : {
              from_id: login?.id,
              to_id: object?.id,
              sent: 1,
              deleted: '0'
            }
          }
        },
        login
      ),
    normalise: (mail, incoming) => ({
      id: mail.id || 0,
      incoming,
      unread: !mail.read,
      name: (incoming ? mail.from?.full_name : mail.to?.full_name) || '-',
      subject: mail.subject || '',
      created_at: mail.created_at || ''
    }),
  },

  ehealth: {
    valueKey: HelpersClientWidgets.communicationEhealthValueKey,
    titleKey: 'coach.submenu.detail.communication_ehealth',
    ctaLabelKey: 'coach.general.actions.to_communication_ehealth',
    ctaId: 'goto_communication_ehealth',
    moduleEnabled: (login) => !!HelpersEhealth.can_check_ehealth(login),
    initialised: (login) => !!HelpersEhealth.ehealth_initialised(login),
    navigateState: (object) => ({
      user_id: {id: object.id, name: `${object.first_name} ${object.last_name}`},
      linked_client: {value: object.id, label: `${object.first_name} ${object.last_name}`, rrn: object.national_number}
    }),
    fetch: (i18n, login, object, box) =>
      fetch_all_with_count<EhealthMail>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'ehealth_mail',
          fields: ['id', 'sender', 'recipients', 'content_data', 'created_at', 'unread'],
          per_page: 5,
          page: 0,
          order: 'id DESC',
          filter: {
            advanced: {
              // The ehealth inbox page's client scoping: box + client_id
              // (the backend matches the client's id or its national number).
              box: box === 'received' ? 'in' : 'sent',
              client_id: object?.id
            }
          }
        },
        login
      ),
    normalise: (mail, incoming) => ({
      id: mail.id || 0,
      incoming,
      unread: !!mail.unread,
      // Received rows show the sender; sent rows the first recipient —
      // falling back to the identifier + quality label (the inbox page's
      // rendering of non-person recipients).
      name: incoming
        ? [mail.sender?.actor?.organizationName, mail.sender?.actor?.firstName, mail.sender?.actor?.lastName].filter(Boolean).join(' ') || '-'
        : (mail.recipients || []).map((x: any) => [
            !!x.person ? x.person.firstName : x.identifiers?.entity,
            !!x.person ? x.person.lastName : `(${HelpersEhealth.get_quality(x.identifiers?.quality || '')})`
          ].join(' ')).join('; ') || '-',
      subject: mail.content_data?.title || '',
      created_at: mail.created_at || ''
    }),
  },

  zorg: {
    valueKey: HelpersClientWidgets.communicationZorgValueKey,
    titleKey: 'coach.submenu.detail.communication_zorg',
    ctaLabelKey: 'coach.general.actions.to_communication_zorg',
    ctaId: 'goto_communication_zorg',
    moduleEnabled: (login) => !!HelpersZorgmail.can_check_zorgmail(login),
    initialised: (login) => !!HelpersZorgmail.zorgmail_initialised(login),
    navigateState: (object) => ({
      user_id: {id: object.id, name: `${object.first_name} ${object.last_name}`},
      linked_client: {value: object.id, label: `${object.first_name} ${object.last_name}`, rrn: object.national_number}
    }),
    fetch: (i18n, login, object, box) =>
      fetch_all_with_count<ZorgMail>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'zorg_mail',
          fields: ['id', 'read', 'message_data', 'created_at'],
          per_page: 5,
          page: 0,
          order: 'created_at DESC',
          filter: {
            search: '',
            // The zorgmail inbox page's client scoping (client_id resolves
            // server-side to the client's national number). Received skips
            // the page's is_read split — one query covers read + unread.
            advanced: box === 'received' ? {
              user_id: login?.id,
              is_deleted: '0',
              is_sending: '0',
              client_id: object?.id
            } : {
              user_id: login?.id,
              is_sent: 1,
              is_deleted: '0',
              client_id: object?.id
            }
          }
        },
        login
      ),
    normalise: (mail, incoming) => ({
      id: mail.id || 0,
      incoming,
      unread: !mail.read,
      // Received rows show the external sender; sent rows the draft's
      // recipients (both straight from the message_data, as the page renders).
      name: incoming
        ? mail.message_data?.header_from || '-'
        : (mail.message_data?.draft?.recipients || []).map((r: any) => r.displayName).filter(Boolean).join(', ') || '-',
      subject: (incoming ? mail.message_data?.header_subject : mail.message_data?.draft?.subject) || '',
      created_at: mail.created_at || ''
    }),
  },
};

type Props = {
  source: SourceKey;
  object: User;
  setPremiumNutriMailOpen?: (b:boolean) => void;
  setPremiumEhealthOpen?: (b:boolean) => void;
  setPremiumZorgmailOpen?: (b:boolean) => void;
  setEhealthInitOpen?: (b:boolean) => void;
  setZorgmailInitOpen?: (b:boolean) => void;
}

export const CoachUserWidgetsCommunicationSmall: FunctionComponent<Props> = ({
  source,
  object,
  setPremiumNutriMailOpen,
  setPremiumEhealthOpen,
  setPremiumZorgmailOpen,
  setEhealthInitOpen,
  setZorgmailInitOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  const cfg = CONFIG[source];

  // Module + init gates (ehealth/zorg also need their credentials set up —
  // the same can_check + initialised pair PictureAndTags uses). Queries run
  // only when both pass; the premium lock only cares about the module.
  const isModule = cfg.moduleEnabled(login, currentPractice);
  const isInitialised = cfg.initialised(login);
  const isEnabled = isModule && isInitialised;

  // Label base for the source's i18n keys (counts plural pairs live next to
  // the widget title keys).
  const labelBase = `coach.submenu.detail.${source === 'nutri' ? 'communication_nutri' : (source === 'ehealth' ? 'communication_ehealth' : 'communication_zorg')}`;

  // Coach-level widget settings (the `counts` summary-line toggle, the
  // `list` last-5 toggle and the `cta` goto-button toggle, all on by
  // default). Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const settingsValue = widgetSettings?.[cfg.valueKey];
  const showCounts = HelpersClientWidgets.getCommunicationSmallCountsEnabled(settingsValue);
  const showList = HelpersClientWidgets.getCommunicationSmallListEnabled(settingsValue);
  const showCta = HelpersClientWidgets.getCommunicationSmallCtaEnabled(settingsValue);

  // The received + sent halves, client-scoped. Each query's `d.count` is the
  // total matching the filter regardless of per_page, so it feeds the
  // summary line while the rows merge into the last-5 list.
  let receivedCount = 0;
  let sentCount = 0;

  const {
    data: receivedMails,
  } = useQuery({
    queryKey: ["communication", source, object?.id, "received"],
    queryFn: () => cfg.fetch(i18n, login, object, 'received'),
    select: (d) => {
      receivedCount = d.count;
      return d.result;
    },
    enabled: isEnabled && !!object?.id && object?.type === "User::User",
  });

  const {
    data: sentMails,
  } = useQuery({
    queryKey: ["communication", source, object?.id, "sent"],
    queryFn: () => cfg.fetch(i18n, login, object, 'sent'),
    select: (d) => {
      sentCount = d.count;
      return d.result;
    },
    enabled: isEnabled && !!object?.id && object?.type === "User::User",
  });

  // Newest-5 across both directions, newest first. Dates compare in Luxon
  // (Europe/Brussels, the pages' zone) with id as a stable tiebreaker.
  const lastFive = (receivedMails || []).map((m) => cfg.normalise(m, true))
    .concat((sentMails || []).map((m) => cfg.normalise(m, false)))
    .sort((a, b) => {
      const aT = DateTime.fromISO(a.created_at || '').setZone("Europe/Brussels");
      const bT = DateTime.fromISO(b.created_at || '').setZone("Europe/Brussels");
      if (aT.valueOf() !== bT.valueOf()) return bT.valueOf() - aT.valueOf();
      return (b.id || 0) - (a.id || 0);
    })
    .slice(0, 5);

  // Date/time caption: time when same day, else "d LLL" — the inbox pages'
  // exact rule, pre-formatted to a string (t()/html single-string rule).
  const fmtMailDate = (iso?: string) => {
    if (!iso) return '';
    const dt = DateTime.fromISO(iso).setZone("Europe/Brussels");
    const now = DateTime.now().setZone("Europe/Brussels");
    return dt.toFormat("D") === now.toFormat("D") ? dt.toFormat("t") : dt.toFormat("d LLL");
  };

  const doCta = () => {
    if (!isModule) {
      if (source === 'ehealth') setPremiumEhealthOpen?.(true);
      else if (source === 'zorg') setPremiumZorgmailOpen?.(true);
      else setPremiumNutriMailOpen?.(true);
      return;
    }
    if (source === 'ehealth' && !isInitialised) {
      setEhealthInitOpen?.(true);
      return;
    }
    if (source === 'zorg' && !isInitialised) {
      setZorgmailInitOpen?.(true);
      return;
    }
    navigate(source === 'nutri' ? '/inbox' : (source === 'ehealth' ? '/ehealth_inbox' : '/zorgmail_inbox'), {state: cfg.navigateState(object)});
  };

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row
        (space-saving — the standard widget CTA pattern), gated by the
        coach's `cta` setting. Premium-locked when the module is off;
        ehealth/zorg open their init dialog instead when the coach hasn't
        set up their credentials. */}
    {showCta && <Button
      label={t(cfg.ctaLabelKey)}
      id={cfg.ctaId}
      text
      size="small"
      premiumLocked={!isModule}
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => doCta()}
    />}

    <Grid container spacing={1} sx={{position: 'relative'}}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t(cfg.titleKey)}</Typography>
        <Divider />
      </Grid>

      {/* Summary counts: one i18n plural pair per metric (received / sent),
          separated by a middot. Only meaningful when at least one mail exists
          in either direction. */}
      {showCounts && receivedCount + sentCount > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {`${t(`${labelBase}_received_count`, {count: receivedCount})} · ${t(`${labelBase}_sent_count`, {count: sentCount})}`}
        </Typography>
      </Grid>}

      {/* Last 5 mails: arrow (up = sent by the practice, down = received),
          counterparty name + date/time on the first line (bold when unread),
          subject in a smaller caption below. A Divider between rows. */}
      {showList && lastFive.map((row, idx) => <React.Fragment key={`${source}_${row.id}_${row.incoming}`}>
        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {row.incoming
            ? <CallReceived sx={{fontSize: '1.1rem', color: 'text.secondary'}} />
            : <CallMade sx={{fontSize: '1.1rem', color: 'text.secondary'}} />}
          <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
            <Grid item xs={12} sx={{display: 'flex', alignItems: 'baseline', gap: 1}}>
              {/* subtitle2 defaults to the theme's fontWeightMedium (700) —
                  pin the weight so unread (700) vs read (400) actually reads. */}
              <Typography variant="subtitle2" noWrap sx={{fontWeight: row.unread ? 700 : 400, flex: 1, minWidth: 0}}>
                {source === "nutri" ? row.subject : row.name}
              </Typography>
              <Typography variant="caption" sx={{color: 'text.secondary', flexShrink: 0}}>
                {fmtMailDate(row.created_at)}
              </Typography>
            </Grid>
            {!!row.subject && source !== "nutri" && <Grid item xs={12}>
              <Typography variant="caption" noWrap sx={{color: 'text.secondary', fontWeight: row.unread ? 700 : 400}}>
                {row.subject}
              </Typography>
            </Grid>}
          </Grid>
        </Grid>
        {idx < lastFive.length - 1 && <Grid item xs={12}>
          <Divider />
        </Grid>}
      </React.Fragment>)}
    </Grid>
  </Paper>;
}
